'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'
import styles from './AllergenTable.module.scss'
import useWindowResize from '@/hooks/use-window-resize'
import useStore from '@/store'
import { measureStickyChromeHeight } from '@/components/ContentWithStickyNavigation/ContentWithStickyNavigation'

type StickyCategoryBandProps = {
  titles: string[]
  // The table's root; holds the column header and the category <tbody>s.
  rootRef: RefObject<HTMLElement | null>
}

// Repeats the red band of the category being scrolled through just below the sticky column header,
// and pushes it out as that category ends. CSS `sticky` can't do this: a sticky band cell is bound
// by the whole table rather than its <tbody>, and below `laptop` the horizontal scroller traps it.
const StickyCategoryBand = ({ titles, rootRef }: StickyCategoryBandProps) => {
  const bandRef = useRef<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const resizeKey = useWindowResize()
  const stickyBannerIsOpen = useStore(state => state.stickyBannerIsOpen)

  useEffect(() => {
    const root = rootRef.current
    const band = bandRef.current
    if (!root || !band) return

    // The visible column header: the mobile bar below `laptop`, the real <thead> above it.
    const bar = root.querySelector<HTMLElement>(`.${styles.stickyBar}`)
    const header = bar && getComputedStyle(bar).display !== 'none' ? bar : root.querySelector('thead')
    const headerHeight = header?.getBoundingClientRect().height ?? 0
    const stickyBottom = measureStickyChromeHeight() + headerHeight
    const bandHeight = band.getBoundingClientRect().height

    // The category <tbody>s turn this into `scroll-margin-top`, so the sticky nav lands a category's
    // band just below the column header instead of under it.
    root.style.setProperty('--allergen-header-height', `${headerHeight}px`)

    band.style.top = `${stickyBottom}px`
    // Takes no room in the flow, so the table sits where it would without the band.
    band.style.marginBottom = `-${bandHeight}px`

    let frame = 0
    const update = () => {
      frame = 0
      let index = -1
      let offset = 0
      root.querySelectorAll<HTMLElement>('[data-allergen-category]').forEach((group, i) => {
        const { top, bottom } = group.getBoundingClientRect()
        if (top <= stickyBottom && bottom > stickyBottom) {
          index = i
          offset = Math.min(0, bottom - stickyBottom - bandHeight)
        }
      })
      band.style.visibility = index === -1 ? 'hidden' : 'visible'
      band.style.transform = `translateY(${offset}px)`
      setActiveIndex(index)
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [rootRef, titles.length, stickyBannerIsOpen, resizeKey])

  return (
    <div
      ref={bandRef}
      className={styles.stickyBand}
      aria-hidden="true"
    >
      {/* Falls back to the first title so the band has its real height before it is shown. */}
      <span className={styles.bandText}>{titles[activeIndex] ?? titles[0]}</span>
    </div>
  )
}

StickyCategoryBand.displayName = 'StickyCategoryBand'

export default StickyCategoryBand
